export interface ProductTypeConfig {
  /** Fabric canvas dimensions (px) — replaces CANVAS_SIZE_MAP */
  canvas: { width: number; height: number; topOffset?: number; leftOffset?: number };

  /** i18n key for the designer's product label (chooseColorLabel) */
  labelKey: string;

  /** Show Front/Back toggle tabs in the designer */
  hasTwoSides: boolean;

  /**
   * "Cup-like" behaviour — applies to CUP, PHONE_CASE, THERMOS_CUP:
   * - Hides color picker in designer
   * - Uses uniform height-based DPI for all sizing math
   * - Auto-submits add-to-cart dialog (no size selection screen)
   * - Skips size pre-fill when navigating from home popular products
   * - Hides gender column in cart detail
   * - Hides fabric material tab in product detail
   * - Hides color/size validation in product detail add-to-cart
   */
  isCupLike: boolean;

  /**
   * Whether to map the physical width to the half circumference (Math.PI * diameter / 2)
   * instead of the diameter itself, and use a separate dpiX for the canvas mapping.
   * True for THERMOS_CUP, false for others.
   */
  useCircumferencePrint?: boolean;

  /**
   * Right-sidebar layout: space-between (cup) vs flex-start (default).
   * Intentionally separate from isCupLike — PHONE_CASE is isCupLike
   * but uses the default flex-start layout, not the cup space-between layout.
   * Maps to: [class.layout-top-align]="!config.useCupLayout" in template.
   */
  useCupLayout: boolean;

  /**
   * Cart grouping: always merge into a single cart line vs split by color+size.
   * CUP and THERMOS_CUP merge; SHIRT and PHONE_CASE split by variant.
   * Maps to: isCupOnly guard in product-detail requestPrepair().
   */
  isGroupCartItem: boolean;

  /** Small canvas → enforce min 4x export multiplier instead of 2x */
  isSmallCanvas: boolean;

  /** Physical size fallbacks (cm) when API data is missing or below MIN_VALID_SIZE */
  physicalDefaults: { length: number; width: number };

  /** Anchor point in hidden-canvas space (800×800) for design value calculation */
  anchor: { neckY: number; shoulderX: number };

  /** i18n key for the material/fabric tab label in product detail page */
  materialTabKey: string;

  /** Hide library/sticker selection tab in the left sidebar */
  hideLibrary?: boolean;

  /** Hide upload personal image tab in the left sidebar */
  hideUpload?: boolean;

  /** Show color picker in designer (defaults to !isCupLike when omitted) */
  hasColorPicker?: boolean;

  /** Show size picker in right sidebar (e.g. for signage or custom sized products) */
  hasSizePicker?: boolean;

  /** Layout style for color picker: 'dots' (default) vs 'list' (1 item per row with name) */
  colorPickerLayout?: 'dots' | 'list';

  /** Free print price (always 0đ print cost, e.g. for Signage products) */
  isFreePrint?: boolean;

  /**
   * Target accessory size (cm) when first added to canvas.
   * Converted to canvas pixels via dpiX/dpiY from getProductSizingParams().
   * Leave undefined to fall back to DEFAULT_IMAGE_DEMENSION (200×200 px).
   */
  defaultAccessorySizeCm?: { width: number; height: number };

  /** i18n key for the main size section heading in product detail page */
  sizeSelectTitleKey?: string;

  /** i18n key for the default/unisex size list label in product detail page (empty string to hide) */
  sizeGroupLabelKey?: string;
}

export const PRODUCT_TYPE_REGISTRY: Record<string, ProductTypeConfig> = {
  SHIRT: {
    canvas: { width: 240, height: 450 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_SHIRT',
    hasTwoSides: true,
    isCupLike: false,
    useCupLayout: false,
    isGroupCartItem: false,
    isSmallCanvas: false,
    physicalDefaults: { length: 70, width: 50 },
    anchor: { neckY: 165, shoulderX: 270 },
    materialTabKey: 'PRODUCT.TABS.FABRIC',
    sizeSelectTitleKey: 'PRODUCT.SELECT_SIZE',
    sizeGroupLabelKey: 'PRODUCT.SIZE_UNISEX',
    // No defaultAccessorySizeCm — falls back to DEFAULT_IMAGE_DEMENSION (200×200 px)
  },
  PHONE_CASE: {
    canvas: { width: 230, height: 540 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_PHONE_CASE',
    hasTwoSides: false,
    isCupLike: true,
    hasSizePicker: false,
    // PHONE_CASE uses flex-start layout (layout-top-align), not the cup space-between layout
    useCupLayout: false,
    isGroupCartItem: false,
    isSmallCanvas: true,
    physicalDefaults: { length: 15, width: 7 },
    anchor: { neckY: 120, shoulderX: 280 },
    materialTabKey: 'PRODUCT.TABS.CASE_MATERIAL',
    sizeSelectTitleKey: 'PRODUCT.SELECT_PHONE_CASE',
    sizeGroupLabelKey: '',
    // No defaultAccessorySizeCm — falls back to DEFAULT_IMAGE_DEMENSION (200×200 px)
  },
  CUP: {
    canvas: { width: 250, height: 280, topOffset: 10, leftOffset: -50 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_CUP',
    hasTwoSides: true,
    isCupLike: true,
    hasSizePicker: false,
    useCircumferencePrint: true,
    useCupLayout: true,
    isGroupCartItem: true,
    isSmallCanvas: true,
    physicalDefaults: { length: 10, width: 8 },
    anchor: { neckY: 270, shoulderX: 225 },
    materialTabKey: 'PRODUCT.TABS.CUP_MATERIAL',
    defaultAccessorySizeCm: { width: 4, height: 4 },
  },
  // ─── Add new product types below — zero changes needed elsewhere ───
  THERMOS_CUP: {
    canvas: { width: 310, height: 614, topOffset: -24 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_THERMOS_CUP',
    hasTwoSides: true,
    isCupLike: true,
    hasSizePicker: true,
    useCircumferencePrint: true,
    useCupLayout: true,
    isGroupCartItem: true,
    isSmallCanvas: true,
    physicalDefaults: { length: 20, width: 7 },
    anchor: { neckY: 70, shoulderX: 245 },
    materialTabKey: 'PRODUCT.TABS.CUP_MATERIAL',
    defaultAccessorySizeCm: { width: 4, height: 4 },
    sizeSelectTitleKey: 'PRODUCT.SELECT_CAPACITY',
    sizeGroupLabelKey: 'PRODUCT.CAPACITY',
  },
  SIGNAGE: {
    canvas: { width: 400, height: 240, topOffset: -10 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_SIGNAGE',
    hasTwoSides: false,
    isCupLike: true,
    hasColorPicker: false,
    hasSizePicker: true,
    colorPickerLayout: 'list',
    useCupLayout: false,
    isGroupCartItem: false,
    isSmallCanvas: false,
    hideLibrary: true,
    isFreePrint: true,
    physicalDefaults: { length: 20, width: 30 },
    anchor: { neckY: 270, shoulderX: 200 },
    materialTabKey: 'PRODUCT.TABS.SIGNAGE_MATERIAL',
    defaultAccessorySizeCm: { width: 8, height: 8 },
    sizeSelectTitleKey: 'PRODUCT.SELECT_SIGNAGE_SIZE',
    sizeGroupLabelKey: 'PRODUCT.DIMENSION',
  },
  BAG: {
    canvas: { width: 320, height: 400, topOffset: 20 },
    labelKey: 'common.DESIGNER.PRODUCT_TYPE_BAG',
    hasTwoSides: true,
    isCupLike: true,
    hasSizePicker: true,
    useCircumferencePrint: false,
    useCupLayout: false,
    isGroupCartItem: false,
    isSmallCanvas: false,
    physicalDefaults: { length: 40, width: 35 },
    anchor: { neckY: 180, shoulderX: 240 },
    materialTabKey: 'PRODUCT.TABS.BAG_MATERIAL',
    sizeSelectTitleKey: 'PRODUCT.SELECT_DIMENSION',
    sizeGroupLabelKey: 'PRODUCT.DIMENSION',
  },
};

/** Returns the config for a product category code. Falls back to SHIRT for unknown codes. */
export function getProductConfig(code: string): ProductTypeConfig {
  const normalized = (code ?? '').toUpperCase();
  if (
    normalized === 'SIGNAGE' ||
    normalized.includes('SIGNAGE') ||
    normalized.includes('BANG_HIEU') ||
    normalized.includes('BIEN_HIEU') ||
    normalized.includes('BIEN_SO_NHA') ||
    normalized.includes('BANG') ||
    normalized.includes('BIEN') ||
    normalized.includes('SIGN')
  ) {
    return PRODUCT_TYPE_REGISTRY['SIGNAGE'];
  }
  if (
    normalized === 'BAG' ||
    normalized === 'TOTE_BAG' ||
    normalized === 'TOTE' ||
    normalized === 'TUI' ||
    normalized.includes('TUI') ||
    normalized.includes('BAG') ||
    normalized.includes('TOTE')
  ) {
    return PRODUCT_TYPE_REGISTRY['BAG'];
  }
  if (
    normalized === 'PHONE_CASE' ||
    normalized.includes('PHONE') ||
    normalized.includes('CASE') ||
    normalized.includes('OP_LUNG') ||
    normalized.includes('OP')
  ) {
    return PRODUCT_TYPE_REGISTRY['PHONE_CASE'];
  }
  if (
    normalized === 'THERMOS_CUP' ||
    normalized.includes('THERMOS') ||
    normalized.includes('BINH')
  ) {
    return PRODUCT_TYPE_REGISTRY['THERMOS_CUP'];
  }
  if (
    normalized === 'CUP' ||
    normalized.includes('CUP') ||
    normalized.includes('COC') ||
    normalized.includes('LY') ||
    normalized.includes('MUG')
  ) {
    return PRODUCT_TYPE_REGISTRY['CUP'];
  }
  return PRODUCT_TYPE_REGISTRY[normalized] ?? PRODUCT_TYPE_REGISTRY['SHIRT'];
}
